import React from "react";
import { Descriptions, Image, Tag, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { formatDateTime, formatDay, formatEuro, formatQuantity } from "../workReports/format";
import { legalBasis, pricingLabels, reasonLabels } from "./labels";

/**
 * What the customer decides on: why, what, how much. `view` has the shape of the server's
 * customerView (or the editor's live equivalent).
 */
const ChangeOrderPreview = ({ view, photoSrc }) => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  if (!view) return null;
  const totals = view.totals || {};
  const decision = view.decision;

  return (
    <div className="co-preview">
      <Typography.Title level={4} className="co-preview-title">
        {t("Change order")} {view.number}
      </Typography.Title>
      <Typography.Text type="secondary">
        {[view.title, view.parent?.label && `${t("to order")} ${view.parent.label}`]
          .filter(Boolean)
          .join(" · ")}
      </Typography.Text>

      <Descriptions size="small" column={1} className="co-preview-facts">
        {view.customer?.name && (
          <Descriptions.Item label={t("Customer")}>{view.customer.name}</Descriptions.Item>
        )}
        {view.project?.name && (
          <Descriptions.Item label={t("Project")}>{view.project.name}</Descriptions.Item>
        )}
        {view.siteAddress && (
          <Descriptions.Item label={t("Site")}>{view.siteAddress}</Descriptions.Item>
        )}
        {view.requestedBy && (
          <Descriptions.Item label={t("Requested by")}>
            {[view.requestedBy, formatDay(view.requestedAt)].filter(Boolean).join(", ")}
          </Descriptions.Item>
        )}
        {view.responseDue && !view.decision?.status && (
          <Descriptions.Item label={t("Answer by")}>
            {formatDay(view.responseDue)}
          </Descriptions.Item>
        )}
      </Descriptions>

      <Typography.Title level={5}>{t("Reason")}</Typography.Title>
      <Typography.Paragraph>
        <strong>{reasonLabels(t)[view.reason]}</strong>{" "}
        <Typography.Text type="secondary">
          ({view.legalBasis || legalBasis(view.contractBasis, view.reason)})
        </Typography.Text>
      </Typography.Paragraph>
      <Typography.Paragraph className="co-preview-text">{view.cause || "—"}</Typography.Paragraph>
      {view.description && (
        <>
          <Typography.Title level={5}>{t("Description of the work")}</Typography.Title>
          <Typography.Paragraph className="co-preview-text">
            {view.description}
          </Typography.Paragraph>
        </>
      )}

      {view.positions?.length > 0 && (
        <>
          <Typography.Title level={5}>{t("Positions")}</Typography.Title>
          <ol className="co-preview-lines">
            {view.positions.map((p, index) => (
              <li key={p._id || index}>
                <div className="co-line-main">
                  <span className="co-line-text">{p.description || "—"}</span>
                  <span className={`co-num${p.net < 0 ? " co-negative" : ""}`}>
                    {formatEuro(p.net, lng)}
                  </span>
                </div>
                <div className="co-line-sub">
                  {formatQuantity(p.quantity, lng)} {p.unit} × {formatEuro(p.unitPrice, lng)}
                </div>
              </li>
            ))}
          </ol>
          <dl className="co-totals">
            <dt>{t("Net")}</dt>
            <dd>{formatEuro(totals.net, lng)}</dd>
            <dt>
              {t("VAT")} {view.taxRate} %
            </dt>
            <dd>{formatEuro(totals.tax, lng)}</dd>
            <dt className="co-gross">{t("Gross")}</dt>
            <dd className="co-gross">{formatEuro(totals.gross, lng)}</dd>
          </dl>
        </>
      )}
      <Typography.Paragraph type="secondary">
        {pricingLabels(t)[view.pricingBasis]}
        {view.pricingBasis === "hourly"
          ? ` – ${t("billed by the hour, proven with signed work reports.")}`
          : ""}
      </Typography.Paragraph>

      {view.photos?.length > 0 && photoSrc && (
        <>
          <Typography.Title level={5}>{t("Photos")}</Typography.Title>
          <div className="co-photo-grid">
            <Image.PreviewGroup>
              {view.photos.map(photo => (
                <figure key={photo._id}>
                  <Image src={photoSrc(photo._id)} alt={photo.caption || ""} />
                  {photo.caption && <figcaption>{photo.caption}</figcaption>}
                </figure>
              ))}
            </Image.PreviewGroup>
          </div>
        </>
      )}

      {decision?.status && (
        <div className={`co-decision co-decision-${decision.status}`}>
          <Tag
            color={{ approved: "success", rejected: "error", ordered: "orange" }[decision.status]}
          >
            {
              {
                approved: t("Approved"),
                rejected: t("Rejected"),
                ordered: t("Ordered, price open")
              }[decision.status]
            }
          </Tag>
          {formatDateTime(decision.at, lng)}
          {decision.name ? ` · ${decision.name}` : ""}
          {decision.note && <div className="co-preview-text">{decision.note}</div>}
          {decision.image && <img src={decision.image} alt={t("Signature")} />}
        </div>
      )}
    </div>
  );
};

export default ChangeOrderPreview;
